export interface CommonAnalyticsDataLayer {
	addInformation: (information: {
		type: string;
		[key: string]: unknown;
	}) => void | Promise<void>;
	getSDKVersion: () => string;
}

interface CommonAnalyticsInit {
	initAnalytics: (initDatalayer?: Record<string, unknown>) => void;
	addHeadScripts: (initDatalayer?: Record<string, unknown>) => void;
	addBodyScripts: () => void;
}

interface CommonAnalyticsAddToCartProduct {
	id: string;
	quantity: number;
	price: number;
}

interface CommonAnalyticsEvents {
	pushStepEvent?: (eventName: string, step: string, stepName: string) => void;
	addToCart?: (
		currency: string,
		products: CommonAnalyticsAddToCartProduct[],
		itemListName?: string,
		productFindingMethod?: string
	) => void;
	viewItem?: (
		itemListName: string,
		productSkus: string[]
	) => void | Promise<void>;
	viewItemList?: (
		itemListName: string,
		productSkus: string[]
	) => void | Promise<void>;
	pdpContentClick?: (params: {
		actionName: "media_click" | "video_click" | "use_and_care_click";
		productSku: string;
		currency: string;
		assetId?: string;
		position?: number;
	}) => void | Promise<void>;
}

interface CommonAnalyticsGlobal {
	init: CommonAnalyticsInit;
	events: CommonAnalyticsEvents & Record<string, unknown>;
	dataLayer: CommonAnalyticsDataLayer;
}

declare global {
	interface Window {
		PamperedChefCommonAnalytics?: CommonAnalyticsGlobal;
	}
}

/**
 * Safely access the common-analytics global.
 * Returns `undefined` during SSR or before the script has loaded.
 */
export function getCommonAnalytics(): CommonAnalyticsGlobal | undefined {
	if (typeof window === "undefined") return undefined;
	return window.PamperedChefCommonAnalytics;
}

export function getDataLayer(): CommonAnalyticsDataLayer | undefined {
	return getCommonAnalytics()?.dataLayer;
}

/**
 * Check whether the common-analytics script has loaded and is available.
 */
export function isAnalyticsReady(): boolean {
	return getCommonAnalytics()?.dataLayer !== undefined;
}

/**
 * Run a callback once common-analytics is ready.
 * Invokes immediately when already loaded; otherwise polls until ready.
 */
export function onAnalyticsReady(callback: () => void): () => void {
	if (typeof window === "undefined") {
		return () => {};
	}

	if (isAnalyticsReady()) {
		callback();
		return () => {};
	}

	const intervalId = window.setInterval(() => {
		if (!isAnalyticsReady()) return;
		window.clearInterval(intervalId);
		callback();
	}, 50);

	return () => window.clearInterval(intervalId);
}
